import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-brand-primary text-white hover:bg-brand-primary-hover hover:-translate-y-0.5 hover:shadow-md",
        destructive:"bg-semantic-error text-white hover:bg-red-700 hover:-translate-y-0.5 hover:shadow-md",
        destructive_outline: "border border-semantic-error bg-transparent text-semantic-error hover:bg-semantic-error hover:text-white hover:-translate-y-0.5 hover:shadow-md",
        outline: "border border-brand-primary bg-transparent text-brand-primary hover:bg-brand-primary hover:text-white hover:-translate-y-0.5 hover:shadow-md",
        secondary: "bg-sky-500 text-white hover:bg-sky-600 hover:-translate-y-0.5 hover:shadow-md",
        tertiary: "bg-cyan-600 text-white hover:bg-cyan-700 hover:-translate-y-0.5 hover:shadow-md",
        ghost: "hover:bg-neutral-100 hover:text-brand-primary hover:-translate-y-0.5",
        link: "text-brand-primary underline-offset-4 hover:underline hover:text-brand-primary-hover",
        whatsapp: "bg-green-700 text-white hover:bg-green-600 hover:-translate-y-0.5 hover:shadow-md",
        whatsapp_outline: "border border-green-700 bg-transparent text-green-700 hover:bg-green-700 hover:text-white hover:-translate-y-0.5 hover:shadow-md",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
